import React, { useState, useEffect, useContext } from "react";
import _ from "lodash";
import { useIntl, FormattedMessage } from "react-intl";

// Services
import AuthenticationService from "../../../services/AuthenticationService";
import { NotificationsContext } from "../../Notifications";
import apiRequest from "../../../services/apiRequest";
import ErrorBoundary from "../../ErrorBoundary";

// Components
import RoleTable from "./RoleTable";
import AddRoleDialog from "./AddRoleDialog";

// UI
import { PlusCircleIcon } from "@heroicons/react/24/solid";
import { PageHeading } from "../../Container";
import Button from "../../Button";
import Badge from "../../Badge";
import UpdateScopes from "./UpdateScopes";
import useRoleTypes from "../Users/hooks/useRoleTypes";

const getColumns = (intl) => [
  {
    title: "ID",
    field: "id",
  },
  {
    title: intl.formatMessage({ id: "name" }),
    field: "name",
    render: ({ name }) => <b>{name}</b>,
  },
  // {
  //   title: "Role type",
  //   field: "role_type_id",
  //   lookup: (roleTypes || []).reduce(
  //     (obj, i) => _.set(obj, [i.id], intl.formatMessage({ id: i.name })),
  //     {}
  //   ),
  // },
  {
    cellStyle: { width: 600, minWidth: 600 },
    headerStyle: { width: 600, minWidth: 600 },
    title: "Scopes",
    field: "scopes",
    editComponent: (props) => <UpdateScopes {...props} />,
    render: ({ scopes }) =>
      !scopes ? (
        "-"
      ) : (
        <div>
          {scopes.split(";").map((s) => (
            <Badge classNames="mb-1 mr-1" text={s} key={s} />
          ))}
        </div>
      ),
  },
];

const Roles = () => {
  const intl = useIntl();
  const [roles, setRoles] = useState();
  const [columns, setColumns] = useState();
  const [showNewRoleDialog, setShowNewRoleDialog] = useState(false);
  const scopes = AuthenticationService.getUserScopes();
  const { push } = useContext(NotificationsContext);

  useEffect(() => {
    setColumns(getColumns(intl));
  }, []);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const roles = await apiRequest.get("/roles");
        setRoles(
          (roles || []).reduce(
            (rolesObj, role) => _.set(rolesObj, [role.id], role),
            {}
          )
        );
      } catch (error) {
        push({
          title: intl.formatMessage({ id: "server_error" }),
          type: "error",
        });
      }
    };

    if (!roles) {
      fetchRoles();
    }
    // eslint-disable-next-line
  }, [roles]);

  const toggleNewRoleDialog = () => setShowNewRoleDialog(!showNewRoleDialog);
  const canCreate = scopes.indexOf("role:create") >= 0;

  return (
    <>
      <PageHeading
        title={intl.formatMessage({ id: "roles" })}
        contentActions={
          canCreate ? (
            <Button
              variant="contained"
              color="primary"
              onClick={toggleNewRoleDialog}
            >
              <PlusCircleIcon
                className="-ml-0.5 mr-2 h-4 w-4"
                aria-hidden="true"
              />
              <FormattedMessage id="add" />
            </Button>
          ) : null
        }
      />
      <ErrorBoundary from="roles-table">
        <RoleTable
          rows={roles}
          setRow={setRoles}
          push={push}
          apiKey="roles"
          roleKey="role"
          columns={columns}
        />
      </ErrorBoundary>
      {canCreate ? (
        <ErrorBoundary from="roles-add-dialog">
          <AddRoleDialog
            open={showNewRoleDialog}
            toggleOpen={toggleNewRoleDialog}
            roles={roles}
            setRoles={setRoles}
          />
        </ErrorBoundary>
      ) : null}
    </>
  );
};

export default Roles;
